import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DecisionCheckbox } from "@/components/agents/configuration-server/stages/DecisionRows";
import { LoadMore } from "@/components/agents/configuration-server/stages/Paging";
import {
  legacyKey,
  orgUnitId,
  type OrgGroupRow,
  type OrgTreeRow,
  type OrgUnitRow,
} from "@/lib/diagnostic-org-tree";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import { useDecisionLabels, type DecisionView } from "./decisionLabels";
import { useOrgLevelLabels, type OrgLevelLabels } from "./orgLevelLabels";
import { ValidationCells, type ValidationEditing } from "./ValidationCells";

/**
 * A seleção do lote. Só existe para quem edita (D9): sem ela, a coluna não existe.
 * `selected` já é a interseção com `selectable` (D12). `pending` são os selecionáveis
 * sem decisão do filtro inteiro, desenhados ou não (D41). `disabled` trava as caixas
 * enquanto o lote grava (D30): o sucesso limpa a seleção, e o que se marcasse
 * nesse intervalo sumiria sem aviso.
 */
export interface OrgSelection {
  selected: ReadonlySet<string>;
  selectable: ReadonlySet<string>;
  pending: ReadonlySet<string>;
  disabled: boolean;
  onToggle(unitId: string, checked: boolean): void;
  onTogglePending(checked: boolean): void;
}

/** Quantas linhas a tabela desenha de cada vez: a árvore aberta passa de milhares (D38). */
export const ORG_PAGE_SIZE = 100;

/**
 * A árvore em tabela, na quebra do template.
 *
 * Só desenha: quem decide o que está aberto é a tela, por `expanded`, para
 * "Expandir tudo" e "Recolher" agirem sobre a árvore inteira.
 *
 * Desenha só as primeiras `limit` linhas visíveis; o limite é da tela, que o
 * sobe em `onLoadMore` quando o fim da tabela se aproxima.
 *
 * Os rótulos são montados aqui, uma vez, e descem para as linhas: cada hook de
 * rótulo faz dezenas de `t()`, e a árvore aberta tem milhares de linhas.
 */
export function OrgStructureTable({
  rows,
  expanded,
  onToggle,
  editing,
  selection,
  limit,
  searching = false,
  onLoadMore,
}: {
  rows: OrgTreeRow[];
  expanded: ReadonlySet<string>;
  onToggle: (key: string) => void;
  editing: ValidationEditing;
  selection?: OrgSelection;
  limit: number;
  /** Há busca ativa: sem linha nenhuma, a tabela diz que nada casa. */
  searching?: boolean;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation();
  const columnCount = selection ? COLUMN_COUNT + 1 : COLUMN_COUNT;
  const levels = useOrgLevelLabels();
  const decisions = useDecisionLabels();
  const visible = useMemo(() => flatten(rows, expanded, 0), [rows, expanded]);
  const hasMore = visible.length > limit;
  const pendingDisabled = !selection || selection.disabled || selection.pending.size === 0;

  return (
    <div className="overflow-x-auto">
      <table data-testid="org-structure-tree" className="w-full min-w-[1080px] border-collapse text-left">
        <thead>
          <tr className="border-b border-[#eef1f5] font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {selection && (
              <th className="w-8 px-3 py-2">
                <DecisionCheckbox
                  label={t("Select all pending")}
                  checked={pendingChecked(selection)}
                  disabled={pendingDisabled}
                  onChange={selection.onTogglePending}
                />
              </th>
            )}
            <th className="px-3 py-2 font-medium">
              {t("Organizational object")}
              {selection && (
                <button
                  type="button"
                  data-testid="org-select-pending"
                  disabled={pendingDisabled}
                  onClick={() => selection.onTogglePending(pendingChecked(selection) !== true)}
                  className="ml-3 text-[11px] font-medium normal-case tracking-normal text-[#08254c] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-[#9aa6b8] disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] focus-visible:ring-offset-1"
                >
                  {t("Select all pending ({{total}})", {
                    total: formatCount(selection.pending.size),
                  })}
                </button>
              )}
            </th>
            <th className="px-3 py-2 font-medium">{t("Legacy key")}</th>
            <th className="px-3 py-2 font-medium">{t("SAP rule / note")}</th>
            <th className="px-3 py-2 font-medium">{t("Matches?")}</th>
            <th className="px-3 py-2 font-medium">{t("Scope")}</th>
            <th className="px-3 py-2 font-medium">{t("Note")}</th>
          </tr>
        </thead>
        <tbody>
          {searching && visible.length === 0 && (
            <tr data-testid="org-search-empty" className="border-b border-[#eef1f5]">
              <td colSpan={columnCount} className="px-3 py-6 text-center text-[13px] text-[#596b85]">
                {t("No object matches the search")}
              </td>
            </tr>
          )}
          {visible.slice(0, limit).map(({ row, depth }) =>
            row.kind === "unit" ? (
              <UnitRow
                key={row.key}
                row={row}
                depth={depth}
                open={expanded.has(row.key)}
                onToggle={onToggle}
                levels={levels}
                decisions={decisions}
                editing={editing}
                selection={selection}
              />
            ) : row.kind === "group" ? (
              <GroupRow
                key={row.key}
                row={row}
                open={expanded.has(row.key)}
                onToggle={onToggle}
                columnCount={columnCount}
              />
            ) : (
              <EmptyRow
                key={row.key}
                levelCode={row.level.level_code}
                levelName={row.level.level_name}
                unreadableTables={row.level.unreadable_tables ?? []}
                depth={depth}
                levels={levels}
                columnCount={columnCount}
              />
            ),
          )}
        </tbody>
      </table>
      {hasMore && (
        <p data-testid="org-rows-count" className="px-3 py-2 text-[11px] text-[#7b8799]">
          {t("Showing {{shown}} of {{total}} rows", {
            shown: formatCount(limit),
            total: formatCount(visible.length),
          })}
        </p>
      )}
      {hasMore && <LoadMore hasMore loading={false} onLoadMore={onLoadMore} />}
    </div>
  );
}

/** Marcada quando todo pendente do filtro está selecionado; indeterminada com qualquer outra seleção. */
function pendingChecked({ selected, pending }: OrgSelection): boolean | "indeterminate" {
  if (pending.size > 0 && [...pending].every((id) => selected.has(id))) return true;
  return selected.size > 0 ? "indeterminate" : false;
}

function flatten(
  rows: OrgTreeRow[],
  expanded: ReadonlySet<string>,
  depth: number,
): Array<{ row: OrgTreeRow; depth: number }> {
  return rows.flatMap((row) => [
    { row, depth },
    ...(row.kind !== "empty" && expanded.has(row.key)
      ? flatten(row.children, expanded, depth + 1)
      : []),
  ]);
}

/**
 * A linha que abre um grupo do fim da árvore: o que o SAP não liga a empresa
 * nenhuma (o grupo de compradores) ou o bloco das organizações de compras, que
 * fica em separado como no CBC.
 */
function GroupRow({
  row,
  open,
  onToggle,
  columnCount,
}: {
  row: OrgGroupRow;
  open: boolean;
  onToggle: (key: string) => void;
  columnCount: number;
}) {
  const { t } = useTranslation();
  const shared = row.group === "shared";
  const label = !shared
    ? t("Purchasing organizations")
    : row.byUsage
      ? t("Purchasing groups in use")
      : t("Not linked to a company in SAP");

  return (
    <tr data-testid={`org-group-${row.group}`} className="border-b border-[#eef1f5] bg-[#eef4fb]">
      <td colSpan={columnCount} className="py-2.5 pr-3" style={indent(0)}>
        <button
          type="button"
          onClick={() => onToggle(row.key)}
          aria-expanded={open}
          aria-label={
            open ? t("Collapse {{name}}", { name: label }) : t("Expand {{name}}", { name: label })
          }
          className="flex items-center gap-1.5 rounded text-[13px] font-semibold text-[#08254c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {`${label} · ${formatCount(row.count)}`}
        </button>
      </td>
    </tr>
  );
}

/** Objeto, chave legado, regra SAP, Confere?, Escopo e Observação, sem a coluna de seleção. */
const COLUMN_COUNT = 6;

/** Recuo por profundidade, como no protótipo; a seta ocupa o lugar mesmo sem filho. */
const indent = (depth: number) => ({ paddingLeft: `${12 + depth * 18}px` });

function UnitRow({
  row,
  depth,
  open,
  onToggle,
  levels,
  decisions,
  editing,
  selection,
}: {
  row: OrgUnitRow;
  depth: number;
  open: boolean;
  onToggle: (key: string) => void;
  levels: OrgLevelLabels;
  decisions: Record<DecisionView, string>;
  editing: ValidationEditing;
  selection?: OrgSelection;
}) {
  const { t } = useTranslation();
  const { node, level } = row;
  const title = node.name ?? node.id;
  const out = node.effective_decision === "out_of_scope";

  return (
    <tr
      data-testid={`org-row-${node.item_type}:${node.item_key}`}
      data-transversal={row.transversal ? "true" : "false"}
      className={cn(
        "border-b border-[#eef1f5] align-top",
        row.transversal && "bg-[#eef4fb]",
      )}
    >
      {selection && (
        <td className="w-8 px-3 py-2.5">
          {selection.selectable.has(orgUnitId(node)) && (
            <DecisionCheckbox
              label={t("Select {{name}}", { name: title })}
              checked={selection.selected.has(orgUnitId(node))}
              disabled={selection.disabled}
              onChange={(checked) => selection.onToggle(orgUnitId(node), checked)}
            />
          )}
        </td>
      )}
      <td className="py-2.5 pr-3" style={indent(depth)}>
        <div className="flex items-start gap-1.5">
          {row.children.length > 0 ? (
            <button
              type="button"
              onClick={() => onToggle(row.key)}
              aria-expanded={open}
              aria-label={
                open
                  ? t("Collapse {{name}}", { name: title })
                  : t("Expand {{name}}", { name: title })
              }
              className="mt-2 rounded text-[#7b8799] hover:text-[#08254c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-[11px] leading-snug text-[#7b8799]">
              {levels.typeLine(level.level_code, level.level_name)}
            </div>
            <div
              className={cn(
                "text-[13px] font-semibold leading-snug text-[#08254c]",
                out && "text-[#9aa5b5] line-through",
              )}
            >
              {title}
            </div>
            {/* #1791: o nome da origem é dado; a correção do consultor no Configuration
                Server aparece à parte, como decisão, e não no lugar dele. */}
            {node.decided_name && (
              <div data-testid="org-decided-name" className="mt-0.5 text-[11px] text-[#596b85]">
                {t("Name decided in the Configuration Server: {{name}}", {
                  name: node.decided_name,
                })}
              </div>
            )}
            {row.parentCount > 1 && (
              <div className="mt-0.5 text-[11px] text-[#596b85]">
                {t("Linked to {{count}} parent units", { count: row.parentCount })}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <span className="inline-block rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[12px] text-[#3c4b61]">
          {legacyKey(row.node)}
        </span>
      </td>
      <td className="px-3 py-2.5 text-[11px] leading-snug text-[#596b85]">
        {level.naming_rule && <div>{level.naming_rule}</div>}
        {!level.maintainable_in_org_app && (
          <div className="font-mono text-[#c0392b]">{t("Not maintained in the Org Structure app")}</div>
        )}
      </td>
      <ValidationCells node={node} title={title} decisions={decisions} editing={editing} />
    </tr>
  );
}

function EmptyRow({
  levelCode,
  levelName,
  unreadableTables,
  depth,
  levels,
  columnCount,
}: {
  levelCode: string;
  levelName: string;
  /** Tabelas do nível que o SAP não liberou: aí "nenhum encontrado" seria afirmar o que não se leu. */
  unreadableTables: string[];
  depth: number;
  levels: OrgLevelLabels;
  columnCount: number;
}) {
  const { t } = useTranslation();
  return (
    <tr data-testid={`org-empty-${levelCode}`} className="border-b border-[#eef1f5]">
      <td colSpan={columnCount} className="py-2.5 pr-3 italic" style={indent(depth)}>
        <div className="pl-5 text-[11px] text-[#7b8799]">
          {levels.typeLine(levelCode, levelName)}
        </div>
        {unreadableTables.length > 0 ? (
          <div className="pl-5 text-[12px] text-[#c25500]">
            {t(
              "SAP did not release reading {{tables}}: there is no way to tell what exists here in the legacy system.",
              { tables: unreadableTables.join(", ") },
            )}
          </div>
        ) : (
          <div className="pl-5 text-[12px] text-[#596b85]">{levels.notFound(levelCode, levelName)}</div>
        )}
      </td>
    </tr>
  );
}
